import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { GlassCard } from './GlassCard';
import { getConsentDefinitions, getConsent, saveConsent } from '../lib/api';

export function ConsentSettings({ userId }) {
  const [defs, setDefs] = useState([]);
  const [state, setState] = useState({});
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!userId) return;
    Promise.all([getConsentDefinitions(), getConsent(userId)])
      .then(([d, c]) => {
        setDefs(d || []);
        setState(c?.consents || {});
      })
      .catch(() => {});
  }, [userId]);

  const toggle = async (def) => {
    const current = state[def.type]?.consented ?? def.required;
    const next = !current;
    setSaving(def.type);
    try {
      const res = await saveConsent(userId, def.type, next);
      setState((s) => ({ ...s, [def.type]: { consent_type: def.type, consented: res.consented, updated_at: res.updated_at } }));
      toast.success(next ? `${def.title} granted` : `${def.title} withdrawn`);
    } catch (e) {
      toast.error('Could not update your preference');
    } finally {
      setSaving(null);
    }
  };

  return (
    <GlassCard className="p-6 mb-5 animate-fade-up" data-testid="consent-settings">
      <div className="flex items-center gap-2 mb-1">
        <div className="grid place-items-center w-9 h-9 rounded-xl bg-[var(--aura-teal)]/12 text-[var(--aura-teal)]"><i className="fa-solid fa-shield-halved" /></div>
        <h3 className="font-display text-lg">Data & privacy</h3>
      </div>
      <p className="text-sm text-[var(--aura-ink-soft)] mb-4">Manage how your data is used. You can change these anytime — every change is recorded.</p>

      <div className="divide-y divide-[var(--aura-line)]">
        {defs.map((def) => {
          const rec = state[def.type];
          const on = rec?.consented ?? def.required;
          return (
            <div key={def.type} className="flex items-start gap-4 py-4" data-testid={`consent-${def.type}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{def.title}</span>
                  {def.required && <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-[var(--aura-purple)]/10 text-[var(--aura-purple)]">Recommended</span>}
                </div>
                <p className="text-sm text-[var(--aura-ink-soft)] mt-0.5">{def.description}</p>
                {rec?.updated_at && (
                  <p className="text-[11px] text-[var(--aura-ink-soft)] mt-1">
                    {on ? 'Granted' : 'Withdrawn'} · {new Date(rec.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                )}
              </div>
              <button
                data-testid={`consent-toggle-${def.type}`}
                onClick={() => toggle(def)}
                disabled={saving === def.type}
                className="relative w-12 h-7 rounded-full transition-base shrink-0 mt-1 disabled:opacity-60"
                style={{ background: on ? 'var(--aura-teal)' : 'var(--aura-line)' }}
                aria-pressed={on}
              >
                <span className="absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-base grid place-items-center" style={{ left: on ? 22 : 2 }}>
                  {saving === def.type && <i className="fa-solid fa-circle-notch fa-spin text-[10px] text-[var(--aura-ink-soft)]" />}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-[var(--aura-ink-soft)] mt-3 flex items-center gap-1.5">
        <i className="fa-solid fa-lock" /> Your data is handled in line with UK GDPR. Withdrawing consent won&apos;t delete existing records.
      </p>
    </GlassCard>
  );
}
